<script setup lang="ts">
  import { computed, onMounted, provide, ref, useTemplateRef } from 'vue'
  import { useUser } from '@gat-ship/user'
  import { testIds } from '@gat-ship/types'
  import { companyCollectionKey } from '@gat-ship/types/injection-keys'
  import {
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription,
    useAppLoading
  } from '@gat-ship/vue-components'
  import { IconPlus, IconLoading } from '@gat-ship/icons'
  import {
    CompaniesList,
    CompanyModalProvider,
    companyLoadingIds,
    useCompanyCollection
  } from '@gat-ship/companies'
  import { useAdminTabClass } from '../../composables/useAdminTabClass'
  import { useCompaniesPanel } from '../../composables/useCompaniesPanel'

  // The global AdministrationModal opens purely off `?admin=companies` and is
  // mounted for every authenticated user, so this panel must gate itself — the
  // router CompaniesView enforces the same check via PageLayout access-props.
  const user = useUser()

  // The panel owns the datasource the same way the routed RouteLayout does:
  // create the collection here and provide it under the shared injection key so
  // the descendant CompaniesList (and the modal provider's commands) read it.
  const collection = useCompanyCollection()
  provide(companyCollectionKey, collection)

  onMounted(() => {
    collection.fetchCompanies()
  })

  const { isLoading: appIsLoading } = useAppLoading()
  const addIsLoading = computed(() => appIsLoading(companyLoadingIds.add))

  // Local (non-deep-linked) view state, mirroring the routed view's Active /
  // Inactive tabs. Kept off the URL so it doesn't clobber the route view's own
  // `?show` query param.
  const activeTab = ref('active')

  const { countCompaniesByStatus, resolveEffectiveTab } = useCompaniesPanel()
  const tabCounts = computed(() =>
    countCompaniesByStatus(collection.companies.value)
  )

  // The Active/Inactive switcher only shows while there are inactive companies.
  // When the last inactive company is reactivated the switcher disappears, so
  // fall back to the active list instead of leaving the view stuck on the now
  // empty inactive filter (matches the retired routed view's default-tab).
  const effectiveTab = computed(() =>
    resolveEffectiveTab(activeTab.value, tabCounts.value.inactive)
  )

  // Add lives behind CompanyModalProvider; reach its showAddCompany via the
  // provider's exposed API (the tenant-users/ports panel precedent).
  const modals =
    useTemplateRef<InstanceType<typeof CompanyModalProvider>>('modals')

  function openAdd() {
    modals.value?.showAddCompany()
  }

  const tabClass = useAdminTabClass(activeTab, 'pill')
</script>

<template>
  <div
    v-if="!user.isTenantAdmin.value"
    class="text-muted-foreground p-10 text-center text-sm"
  >
    You don't have permission to manage companies.
  </div>
  <CompanyModalProvider v-else ref="modals">
    <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
         row off the modal edge (the tenant-users panel precedent). -->
    <div class="flex flex-col gap-6 pb-5 pt-3">
      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle
            >Companies
            <Button
              v-if="user.isTenantAdmin.value"
              class="ml-auto shrink-0"
              :disabled="addIsLoading"
              :data-testid="testIds.company.addButton"
              @click="openAdd"
            >
              <IconLoading v-if="addIsLoading" class="size-4 shrink-0" />
              <IconPlus v-else class="size-4" />
              Add Company
            </Button></ModalSectionHeaderTitle
          >
          <ModalSectionHeaderDescription>
            Manage registered companies that are legal entities tied to your
            agency business.
          </ModalSectionHeaderDescription>
        </ModalSectionHeader>

        <div v-if="tabCounts.inactive > 0" class="flex items-center gap-2">
          <div class="bg-muted flex items-center gap-1 rounded-lg p-1">
            <button
              type="button"
              :class="tabClass('active')"
              :data-testid="testIds.company.companiesList.tabs.active"
              @click="activeTab = 'active'"
            >
              Active
            </button>
            <button
              type="button"
              :class="tabClass('inactive')"
              :data-testid="testIds.company.companiesList.tabs.inactive"
              @click="activeTab = 'inactive'"
            >
              Inactive
            </button>
          </div>
        </div>
        <CompaniesList :active-tab="effectiveTab" />
      </ModalSection>
    </div>
  </CompanyModalProvider>
</template>
